import { useRef, useState, useEffect, useMemo } from 'react';
import JsBarcode from 'jsbarcode';
import xiaomaLogo from 'assets/logo-new.svg';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ReceiptDto } from 'types/oms';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getTimezoneAbbr } from 'utils/getTimezoneByStockPlace';

import {
  weekDetail,
  TICKET_ACCESSORIES_CONFIRM_EN,
  TICKET_ACCESSORIES_CONFIRM_JA,
  TICKET_PAYMENT_NOTICE_EN,
  TICKET_PAYMENT_NOTICE_JA,
  TICKET_STORE_TEXT_JA,
  TICKET_STORE_TEXT_EN,
  TICKET_STORE_TEXT_FR,
  TICKET_PAYMENT_NOTICE_FR,
  TICKET_ACCESSORIES_CONFIRM_FR,
  TICKET_STORE_TEXT_ZH_CN,
  TICKET_PAYMENT_NOTICE_ZH_CN,
  TICKET_ACCESSORIES_CONFIRM_ZH_CN,
} from './utils';
import { thousands } from 'utils/tools';
import { STOCK_PLACE_MAP } from 'constant/stock-place';
import { CURRENCY_MAP } from 'constant/currency';
import { ORDER_STATUS_MAP } from 'constant/oms-order-status';
import { LANGUAGE_MAP } from 'constant/language';
import { shouldDisplayFranceTaxToCustomer } from 'constants/france-tax';

dayjs.extend(utc);
dayjs.extend(timezone);

const HR = () => <div className="border-black border-b border-dashed"></div>;

function Receipt() {
  const barCodeRef = useRef(null);
  const location = useLocation();
  const [state, setState] = useState<ReceiptDto>();

  useEffect(() => {
    setTimeout(() => {
      window.print();
      window.close();
    }, 666);
  }, []);

  const stockPlace = state?.stockPlace;
  const cumulativePoints = state?.cumulativePoints;

  // 获取URL传递参数
  useEffect(() => {
    // 获取参数
    const body = decodeURIComponent(
      queryString.parse(window.location.search).body as string,
    );
    const bodyParse = JSON.parse(body) as ReceiptDto;
    setState(bodyParse);
  }, [location]);

  const lang = useMemo(() => {
    const actualCurrency = state?.actualCurrency;

    if (!actualCurrency) return LANGUAGE_MAP.EN;
    if (actualCurrency === CURRENCY_MAP.JPY) return LANGUAGE_MAP.JA;
    if (actualCurrency === CURRENCY_MAP.EUR) return LANGUAGE_MAP.FR;
    if (actualCurrency === CURRENCY_MAP.CNY) return LANGUAGE_MAP.ZH_CN;

    return LANGUAGE_MAP.EN;
  }, [state?.actualCurrency]);

  const isDepositReceipt = useMemo(() => {
    return (
      state?.orderType === 2 && state?.status === ORDER_STATUS_MAP.TO_BE_PAID
    );
  }, [state?.orderType, state?.status]);

  const showSignField = !!state?.addSign;

  // 条形码
  useEffect(() => {
    const orderId = state?.id;
    const options = {
      fontSize: 0,
      height: 50,
    };
    if (orderId) {
      JsBarcode(barCodeRef.current, `${orderId}`, options);
    }
  }, [state]);

  // 格式化时间
  const getDateTimeEle = useMemo(() => {
    const createTime =
      state?.deliveryTime || state?.paymentTime || state?.createTime;
    const weekDay = dayjs(createTime).day();
    const week = weekDetail[lang][weekDay];
    const day = dayjs(createTime).format(
      lang === 'ja' ? 'YYYY年MM月DD日' : 'YYYY-MM-DD',
    );
    const time = dayjs(createTime).format(lang === 'ja' ? 'HH時mm分' : 'HH:mm');
    const tzAbbr = getTimezoneAbbr(dayjs.tz.guess());

    return (
      <div>
        {day}
        <span className="mx-2">{week}</span>
        {time}
        {tzAbbr ? <span className="ml-1">({tzAbbr})</span> : null}
      </div>
    );
  }, [lang, state?.createTime, state?.deliveryTime, state?.paymentTime]);

  // 价格符号
  const currency = useMemo(() => {
    switch (stockPlace) {
      case STOCK_PLACE_MAP.JAPAN:
      case STOCK_PLACE_MAP.UMEDA:
        return '¥';
      case STOCK_PLACE_MAP.HONGKONG:
        return 'HKD';
      case STOCK_PLACE_MAP.SINGAPORE_ASU:
      case STOCK_PLACE_MAP.SINGAPORE_ASU_GST:
      case STOCK_PLACE_MAP.SINGAPORE_GX:
        return 'SGD';
      case STOCK_PLACE_MAP.DUBAI:
        return 'AED';
      case STOCK_PLACE_MAP.FRANCE:
        return 'EUR';
      case STOCK_PLACE_MAP.SHANG_HAI:
        return 'CNY';
      case STOCK_PLACE_MAP.NEW_YORK:
        return 'USD';
      default:
        return stockPlace || 'Unknown';
    }
  }, [stockPlace]);
  const currencySeparator = useMemo(() => {
    return lang === LANGUAGE_MAP.FR ? ' ' : ',';
  }, [lang]);
  const currencyGap = ' ';

  // 实际价格
  const amountActual = useMemo(() => {
    if (state?.receivedAmount !== undefined)
      return thousands(state?.receivedAmount, currencySeparator);

    return thousands(state?.payAmountActualCurrency || 0, currencySeparator);
  }, [
    currencySeparator,
    state?.payAmountActualCurrency,
    state?.receivedAmount,
  ]);

  // 总税（MARGIN 差额税对顾客隐藏；银座(JP) / 梅田为 0 也展示）
  const totalTaxAmount = useMemo(() => {
    if (!shouldDisplayFranceTaxToCustomer(state?.taxRegime)) {
      return '';
    }
    const tax = state?.totalTaxAmount;
    const isJapanStore =
      stockPlace === STOCK_PLACE_MAP.JAPAN ||
      stockPlace === STOCK_PLACE_MAP.UMEDA;
    const isJapanZero = isJapanStore && tax === 0;
    if (!tax && !isJapanZero) {
      return '';
    }

    return thousands(tax || 0, currencySeparator);
  }, [currencySeparator, state?.taxRegime, state?.totalTaxAmount, stockPlace]);

  // 担当
  const staffName = useMemo(() => {
    return state?.staffName || '';
  }, [state?.staffName]);

  // 优惠金额
  const discountAmountValue = useMemo(() => {
    return state?.promotionAmount || 0;
  }, [state?.promotionAmount]);

  const discountAmountText = useMemo(() => {
    return thousands(discountAmountValue, currencySeparator);
  }, [currencySeparator, discountAmountValue]);

  // 使用积分
  const usePoints = useMemo(() => {
    return state?.useIntegration || 0;
  }, [state]);

  // 获得积分
  const earnPoints = useMemo(() => {
    return Math.floor(state?.integration || 0);
  }, [state]);

  const unpaidBalanceText = useMemo(() => {
    const payAmount = state?.payAmountActualCurrency || 0;
    const received = state?.receivedAmount || 0;
    const unpaid = Math.max(0, payAmount - received);
    return thousands(unpaid, currencySeparator);
  }, [
    currencySeparator,
    state?.payAmountActualCurrency,
    state?.receivedAmount,
  ]);

  // 公司信息
  const CompanyEle = useMemo(() => {
    let address: string[] = [];

    switch (stockPlace) {
      case STOCK_PLACE_MAP.JAPAN:
        address = [
          'GINZA XIAOMA',
          '東京都中央区銀座1丁目8-21',
          '第21中央ビル1F・2F',
          '03-6264-5267',
          '登録番号: T6011101072905',
        ];
        break;

      case STOCK_PLACE_MAP.UMEDA:
        address = [
          'XIAOMA 梅田店',
          '〒530-0001',
          '大阪府大阪市北区梅田1丁目8-17',
          '大阪第一生命ビル1階',
          '06-6467-8153',
          '登録番号: T6011101072905',
        ];
        break;

      case STOCK_PLACE_MAP.HONGKONG:
        address = [
          'ASU Company Limited',
          `G/F, Pacific House, 20B`,
          `Queen's Road Central, Hong Kong`,
          `(+852)2662-9721`,
        ];
        break;

      case STOCK_PLACE_MAP.SINGAPORE_ASU:
      case STOCK_PLACE_MAP.SINGAPORE_ASU_GST:
        address = [
          'ASU COMPANY PTE. LTD',
          '#23-05, Ngee Ann City Tower B',
          '391B Orchard Road',
          '(+65)6530-3529',
        ];
        break;

      case STOCK_PLACE_MAP.SINGAPORE_GX:
        address = [
          'GINZA XIAOMA',
          '#23-05, Ngee Ann City Tower B',
          '391B Orchard Road',
          '(+65)6530-3529',
        ];
        break;

      case STOCK_PLACE_MAP.DUBAI:
        address = [
          'XIAOMA DUBAI',
          'Room102, 32 Marasi Drive Street',
          'The Binary by Omniyat, Business Bay',
          'Dubai United Arab Emirates',
          '00971 4 5540680',
        ];
        break;

      case STOCK_PLACE_MAP.FRANCE:
        address = [
          'XIAOMA PARIS',
          '9 Rue de la Paix, 75002',
          'Paris, France',
          '+33 0140280349',
        ];
        break;

      case STOCK_PLACE_MAP.SHANG_HAI:
        address = [
          'XIAOMA 上海',
          '上海市黄浦区淮海中路',
          '222号607室',
          '+86 153 0162 1883',
        ];
        break;

      case STOCK_PLACE_MAP.NEW_YORK:
        address = [
          'XIAOMA NEW YORK',
          'Trump Tower, Suite 1901',
          '725 Fifth Avenue',
          'New York, NY 10022',
          'United States',
          '+1 9177488152',
          'Mon-Sat 10:00-19:00',
        ];
        break;

      default:
        address = [`Please config the address for stock place: ${stockPlace}`];
        break;
    }

    return (
      <>
        {Object.values(address).map((value, i) => (
          <div key={i} className="leading-4">
            {value}
          </div>
        ))}
      </>
    );
  }, [stockPlace]);

  const subtotal = useMemo(() => {
    const omsOrderItems = state?.omsOrderItems || [];
    return omsOrderItems.reduce((acc, curr) => {
      return (
        acc +
        (curr.productPriceActualCurrency || 0) * (curr.productQuantity || 1)
      );
    }, 0);
  }, [state?.omsOrderItems]);

  // 渲染商品
  const getProductEle = useMemo(() => {
    if (!state) return;
    const { omsOrderItems } = state;

    return (
      <div>
        <div className="mb-2">{getDateTimeEle}</div>
        {omsOrderItems.map((d) => {
          return (
            <div className="mb-3 last-of-type:mb-0" key={d.productId}>
              <div className="flex justify-between">
                <div>{d.productId}</div>
                {!!d.isTaxFree &&
                  shouldDisplayFranceTaxToCustomer(state?.taxRegime) && (
                    <div>
                      {
                        {
                          [LANGUAGE_MAP.JA]: '免税',
                          [LANGUAGE_MAP.EN]: 'Tax Free',
                          [LANGUAGE_MAP.FR]: 'Duty Free',
                          [LANGUAGE_MAP.ZH_CN]: '免税',
                        }[lang]
                      }
                    </div>
                  )}
              </div>

              <div className="flex justify-between">
                <div className="whitespace-pre-line">
                  {d.productName}
                  <span className="ml-2">x{d.productQuantity}</span>
                </div>
                <div className="shrink-0 text-right">
                  <div>
                    {currency}
                    {currencyGap}
                    {thousands(
                      d.productPriceActualCurrency * d.productQuantity,
                      currencySeparator,
                    )}
                  </div>
                </div>
              </div>

              <div className="px-2 py-1">
                {d.accessories?.map((accessory) => (
                  <div key={`${d.productId}-${accessory}`}>{accessory}</div>
                ))}

                <div className="flex mt-4">
                  <span>Note:</span>
                  <span className="w-full border-b border-black" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [state, getDateTimeEle, lang, currency, currencyGap, currencySeparator]);

  return (
    <div className="w-[80mm] py-6 px-4 bg-white text-black relative">
      <div className="mb-4">
        <img
          alt="logo"
          src={xiaomaLogo}
          className="h-full w-3/4 mx-auto block text-black"
        />

        {stockPlace === STOCK_PLACE_MAP.JAPAN && (
          <p className="text-center text-[8pt] mt-2">ASU BRAND株式会社</p>
        )}
      </div>

      <div className="mb-[4pt]">{CompanyEle}</div>

      <HR />

      <div className="my-3">
        <div className="text-3xl text-center mb-3">
          【
          {
            {
              [LANGUAGE_MAP.JA]: '領 収 書',
              [LANGUAGE_MAP.EN]: 'Receipt',
              [LANGUAGE_MAP.FR]: 'Reçu',
              [LANGUAGE_MAP.ZH_CN]: '收据',
            }[lang]
          }
          】
        </div>
        {getProductEle}
      </div>

      <HR />

      <div className="my-3">
        <div className="flex justify-between my-[4px]">
          <div>
            {
              {
                [LANGUAGE_MAP.JA]: '商品金額',
                [LANGUAGE_MAP.EN]: 'Subtotal',
                [LANGUAGE_MAP.FR]: 'Subtotal',
                [LANGUAGE_MAP.ZH_CN]: '商品金额',
              }[lang]
            }
          </div>
          <div>
            {currency}
            {currencyGap}
            {thousands(subtotal, currencySeparator)}
          </div>
        </div>
        {!!state?.freightAmountActualCurrency ? (
          <div className="flex justify-between my-[4px]">
            <div>
              {
                {
                  [LANGUAGE_MAP.JA]: '送料',
                  [LANGUAGE_MAP.EN]: 'Shipping',
                  [LANGUAGE_MAP.FR]: 'Frais de port',
                  [LANGUAGE_MAP.ZH_CN]: '配送费',
                }[lang]
              }
            </div>
            <div>
              {currency}
              {currencyGap}
              {thousands(state?.freightAmountActualCurrency, currencySeparator)}
            </div>
          </div>
        ) : null}

        {/* 优惠金额 */}
        {!!discountAmountValue && (
          <div className="flex justify-between my-[4px]">
            <div>
              {
                {
                  [LANGUAGE_MAP.JA]: '割引額',
                  [LANGUAGE_MAP.EN]: 'Discount',
                  [LANGUAGE_MAP.FR]: 'Réduc',
                  [LANGUAGE_MAP.ZH_CN]: '折扣金额',
                }[lang]
              }
            </div>
            <div>
              -{currency}
              {currencyGap}
              {discountAmountText}
            </div>
          </div>
        )}

        {/* 使用积分 */}
        {!!usePoints && (
          <div className="flex justify-between my-[4px]">
            <div>
              {
                {
                  [LANGUAGE_MAP.JA]: '利用ポイント',
                  [LANGUAGE_MAP.EN]: 'Use points',
                  [LANGUAGE_MAP.FR]: 'Points utilisés',
                  [LANGUAGE_MAP.ZH_CN]: '使用积分',
                }[lang]
              }
            </div>
            <div>-P{thousands(usePoints, currencySeparator)}</div>
          </div>
        )}

        {/* 使用优惠券 */}
        {!!state?.couponAmount && (
          <div className="flex justify-between my-[4px]">
            <div>
              {
                {
                  [LANGUAGE_MAP.JA]: 'クーポン',
                  [LANGUAGE_MAP.EN]: 'Coupon',
                  [LANGUAGE_MAP.FR]: 'Coupon',
                  [LANGUAGE_MAP.ZH_CN]: '优惠券',
                }[lang]
              }
            </div>
            <div>-￥{thousands(state?.couponAmount, currencySeparator)}</div>
          </div>
        )}

        {!!totalTaxAmount && (
          <div className="flex justify-between my-[4px]">
            <div>
              {stockPlace === STOCK_PLACE_MAP.NEW_YORK
                ? '(Sales Tax 8.875%)'
                : {
                    [LANGUAGE_MAP.JA]: '消費税',
                    [LANGUAGE_MAP.EN]: 'Tax',
                    [LANGUAGE_MAP.FR]: 'TVA',
                    [LANGUAGE_MAP.ZH_CN]: '消费税',
                  }[lang]}
            </div>
            <div>
              {currency}
              {currencyGap}
              {totalTaxAmount}
            </div>
          </div>
        )}

        {/* 应付总额 */}
        <div className="flex justify-between my-[4px]">
          <div>
            {
              {
                [LANGUAGE_MAP.JA]: 'お支払い合計',
                [LANGUAGE_MAP.EN]: 'Total',
                [LANGUAGE_MAP.FR]: 'Montant du paiement',
                [LANGUAGE_MAP.ZH_CN]: '应付总额',
              }[lang]
            }
          </div>
          <div>
            {currency}
            {currencyGap}
            {thousands(state?.payAmountActualCurrency || 0, currencySeparator)}
          </div>
        </div>

        {/* 购买商品的总金额 */}
        <div className="flex justify-between my-[4px]">
          <div>
            {
              {
                [LANGUAGE_MAP.JA]: 'お預かり',
                [LANGUAGE_MAP.EN]: 'Paid',
                [LANGUAGE_MAP.FR]: 'Payé',
                [LANGUAGE_MAP.ZH_CN]: '收到金额',
              }[lang]
            }
          </div>
          <div>
            {currency}
            {currencyGap}
            {amountActual}
          </div>
        </div>

        {/* 支付方式 */}
        {!!state?.payList &&
          !!state?.payList?.length &&
          state?.payList?.map((d) => (
            <div key={d.name} className="flex justify-between my-[4px]">
              <div>（{d.name}）</div>
              <div>
                {currency}
                {currencyGap}
                {thousands(d.amount, currencySeparator)}
              </div>
            </div>
          ))}

        <div className="flex justify-between my-[4px]">
          <div>
            {isDepositReceipt
              ? {
                  [LANGUAGE_MAP.JA]: '未払い残額',
                  [LANGUAGE_MAP.EN]: 'Unpaid balance',
                  [LANGUAGE_MAP.FR]: 'Solde restant dû',
                  [LANGUAGE_MAP.ZH_CN]: '未支付余额',
                }[lang]
              : {
                  [LANGUAGE_MAP.JA]: 'お釣り',
                  [LANGUAGE_MAP.EN]: 'Change',
                  [LANGUAGE_MAP.FR]: 'Monnaie',
                  [LANGUAGE_MAP.ZH_CN]: '找零',
                }[lang]}
          </div>
          <div>
            {currency}
            {currencyGap}
            {isDepositReceipt
              ? unpaidBalanceText
              : thousands(state?.changeAmount, currencySeparator)}
          </div>
        </div>

        {/* 获得积分 */}
        {!!earnPoints && (
          <div className="flex justify-between my-[4px]">
            <div>
              {
                {
                  [LANGUAGE_MAP.JA]: '獲得ポイント',
                  [LANGUAGE_MAP.EN]: 'Earn points',
                  [LANGUAGE_MAP.FR]: 'Points gagnés',
                  [LANGUAGE_MAP.ZH_CN]: '获得积分',
                }[lang]
              }
            </div>
            <div>P{thousands(earnPoints, currencySeparator)}</div>
          </div>
        )}

        {
          <>
            <div className="flex justify-between my-[4px]">
              <div>
                【
                {
                  {
                    [LANGUAGE_MAP.JA]: '累計ポイント',
                    [LANGUAGE_MAP.EN]: 'Cumulative points',
                    [LANGUAGE_MAP.FR]: 'Points cumulés',
                    [LANGUAGE_MAP.ZH_CN]: '累计积分',
                  }[lang]
                }
                】<sup>※</sup>
              </div>
              <div>【P{thousands(cumulativePoints, currencySeparator)}】</div>
            </div>
            <div className="text-[10px] my-[2px]">
              {
                {
                  [LANGUAGE_MAP.JA]:
                    '累計ポイントは印刷時点の会員ポイント残高です',
                  [LANGUAGE_MAP.EN]: 'Cumulative points shown as of print time',
                  [LANGUAGE_MAP.FR]: `Points cumulés affichés au moment de l'impression`,
                  [LANGUAGE_MAP.ZH_CN]: '累计积分显示为打印时的会员积分余额',
                }[lang]
              }
            </div>
          </>
        }
      </div>
      <HR />

      <div className="my-3 text-right">
        {
          {
            [LANGUAGE_MAP.JA]: '担当者',
            [LANGUAGE_MAP.EN]: 'Accountable',
            [LANGUAGE_MAP.FR]: 'Responsable',
            [LANGUAGE_MAP.ZH_CN]: '负责人',
          }[lang]
        }
        ：{staffName}
      </div>

      <HR />

      <div className="mt-4">
        <div className="mb-1 whitespace-pre-line text-xs">
          {
            {
              [LANGUAGE_MAP.JA]: TICKET_STORE_TEXT_JA,
              [LANGUAGE_MAP.EN]: TICKET_STORE_TEXT_EN,
              [LANGUAGE_MAP.FR]: TICKET_STORE_TEXT_FR,
              [LANGUAGE_MAP.ZH_CN]: TICKET_STORE_TEXT_ZH_CN,
            }[lang]
          }
        </div>

        {isDepositReceipt && (
          <div className="mb-2 whitespace-pre-line text-xs">
            {
              {
                [LANGUAGE_MAP.JA]: TICKET_PAYMENT_NOTICE_JA,
                [LANGUAGE_MAP.EN]: TICKET_PAYMENT_NOTICE_EN,
                [LANGUAGE_MAP.FR]: TICKET_PAYMENT_NOTICE_FR,
                [LANGUAGE_MAP.ZH_CN]: TICKET_PAYMENT_NOTICE_ZH_CN,
              }[lang]
            }
          </div>
        )}
        <div className="mb-2 whitespace-pre-line text-xs">
          {
            {
              [LANGUAGE_MAP.JA]: TICKET_ACCESSORIES_CONFIRM_JA,
              [LANGUAGE_MAP.EN]: TICKET_ACCESSORIES_CONFIRM_EN,
              [LANGUAGE_MAP.FR]: TICKET_ACCESSORIES_CONFIRM_FR,
              [LANGUAGE_MAP.ZH_CN]: TICKET_ACCESSORIES_CONFIRM_ZH_CN,
            }[lang]
          }
        </div>
        <div className="barCode flex justify-center items-center">
          <svg
            ref={barCodeRef}
            width={200}
            height={100}
            style={{ width: 200, height: 100 }}
          />
        </div>
        <div className="text-center">
          {
            {
              [LANGUAGE_MAP.JA]: '受注No.',
              [LANGUAGE_MAP.EN]: 'Order No.',
              [LANGUAGE_MAP.FR]: 'Commande N° ',
              [LANGUAGE_MAP.ZH_CN]: '订单号',
            }[lang]
          }
          {state?.id}
          {isDepositReceipt
            ? {
                [LANGUAGE_MAP.JA]: '（内金注文）',
                [LANGUAGE_MAP.EN]: '（Deposit）',
                [LANGUAGE_MAP.FR]: '（Acompte）',
                [LANGUAGE_MAP.ZH_CN]: '（定金订单）',
              }[lang]
            : ''}
        </div>
        {showSignField && (
          <div className="mt-4">
            <div className="text-xs">
              {
                {
                  [LANGUAGE_MAP.JA]: '署名',
                  [LANGUAGE_MAP.EN]: 'Sign',
                  [LANGUAGE_MAP.FR]: 'Signature',
                  [LANGUAGE_MAP.ZH_CN]: '签名',
                }[lang]
              }
              :
            </div>
            <div className="mt-2 border-b border-black" />
          </div>
        )}
      </div>
    </div>
  );
}

export default Receipt;
